// types.ts – Types & normalisation pour les APIs /public/demos (prospect)

export type PublicDemosMetaItem = { value: string; label: string };

export interface PublicDemosMetaResponse {
  user_types: PublicDemosMetaItem[];
  meeting_platforms: PublicDemosMetaItem[];
}

/** Valeurs par défaut si l’API ne renvoie rien (évite un menu vide côté UI). */
export const DEFAULT_PUBLIC_DEMOS_USER_TYPES: PublicDemosMetaItem[] = [
  { value: 'individual', label: 'Particulier' },
  { value: 'professional', label: 'Professionnel' },
  { value: 'dealer', label: 'Concessionnaire / distributeur' },
  { value: 'company', label: 'Entreprise / flotte' },
];

function coerceMetaItem(raw: unknown): PublicDemosMetaItem | null {
  if (raw == null || typeof raw !== 'object') return null;
  const o = raw as Record<string, unknown>;
  const value = o.value ?? o.id ?? o.key ?? o.code;
  const label = o.label ?? o.name ?? o.title ?? o.description;
  if (value == null || String(value).trim() === '') return null;
  const v = String(value);
  return { value: v, label: String(label ?? v) };
}

function normalizeMetaItems(arr: unknown): PublicDemosMetaItem[] {
  if (Array.isArray(arr)) {
    return arr.map(coerceMetaItem).filter((x): x is PublicDemosMetaItem => x !== null);
  }
  // Certains backends exposent un "map" { [value]: label }.
  if (arr && typeof arr === 'object') {
    const entries = Object.entries(arr as Record<string, unknown>);
    return entries
      .map(([value, label]) => ({
        value: String(value),
        label: String(label ?? value),
      }))
      .filter((x) => x.value.trim() !== '');
  }
  return [];
}

/** Accepte snake_case, camelCase, items { id/name }, ou corps enveloppé dans `data`. */
export function normalizePublicDemosMeta(raw: unknown): PublicDemosMetaResponse {
  const root =
    raw &&
    typeof raw === 'object' &&
    'data' in (raw as object) &&
    (raw as { data: unknown }).data != null &&
    typeof (raw as { data: unknown }).data === 'object'
      ? (raw as { data: unknown }).data
      : raw;

  if (root == null || typeof root !== 'object') {
    return { user_types: [], meeting_platforms: [] };
  }

  const r = root as Record<string, unknown>;
  const userTypes = r.user_types ?? r.userTypes ?? r.user_type_options ?? r.userTypeOptions;
  const meetingPlatforms =
    r.meeting_platforms ?? r.meetingPlatforms ?? r.platforms ?? r.meetingPlatformsOptions;

  return {
    user_types: normalizeMetaItems(userTypes),
    meeting_platforms: normalizeMetaItems(meetingPlatforms),
  };
}

export type PublicDemoAvailabilityDaySlot = {
  id: string;
  owner_id: string;
  start_time: string;
  end_time: string;
};

export type PublicDemoAvailabilityDay = {
  date: string;
  slots: PublicDemoAvailabilityDaySlot[];
};

export type PublicDemoAvailabilityFlatSlot = {
  id: string;
  owner_id: string;
  date: string;
  start_time: string;
  end_time: string;
};

export interface PublicDemoAvailabilityResponse {
  days: PublicDemoAvailabilityDay[];
  slots: PublicDemoAvailabilityFlatSlot[];
  timezone_hint: string;
  month: number;
  year: number;
  date_from: string;
  date_to: string;
}

export interface PublicDemoSession {
  id: string;
  step: number;
  expires_at: string;
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  user_type: string;
  timezone: string;
  slot_id: string;
  meeting_platform: string;
  notes: string;
  email_reminder_enabled: boolean;
}

export interface PublicDemoSessionsResponse {
  results: PublicDemoSession[];
  count: number;
}

export type PublicDemoCreateSessionBody = {
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  user_type: string;
  timezone: string;
};

export type PublicDemoPatchSessionBody = Partial<PublicDemoCreateSessionBody> & {
  slot_id?: string;
  notes?: string;
  meeting_platform?: string;
  email_reminder_enabled?: boolean;
  confirmed?: boolean;
};

export interface PublicDemoPatchSessionResponse {
  session: PublicDemoSession;
  booking: {
    call_id: string;
    meeting_link: string;
    scheduled_date: string;
    start_time: string;
    end_time: string;
    meeting_platform: string;
    status: string;
  };
}

export type ApiResult<T> =
  | { ok: true; data: T }
  | { ok: false; status: number; error: string };

export type FetchAvailabilityParams =
  | {
      month: number;
      year: number;
      timezone?: string | null;
      date_from?: never;
      date_to?: never;
    }
  | {
      date_from: string;
      date_to: string;
      timezone?: string | null;
      month?: never;
      year?: never;
    };

export type AdminProspectSlotDto = {
  id: string;
  owner_id: string;
  date: string; // YYYY-MM-DD
  start_time: string; // HH:MM
  end_time: string; // HH:MM
  is_active: boolean;
  is_day_disabled: boolean;
  created_at: string;
  updated_at: string;
};

export type AdminProspectDisabledDayDto = {
  date: string; // YYYY-MM-DD
  is_disabled: boolean;
};

export type AdminProspectSlotsResponseDto = {
  results: AdminProspectSlotDto[];
  disabled_days: AdminProspectDisabledDayDto[];
};

export type AdminProspectDayUpsertResponseDto = {
  id: string;
  owner_id: string;
  date: string;
  is_disabled: boolean;
};

export type AdminProspectSlotsBulkBody = {
  date_from: string; // YYYY-MM-DD
  date_to: string; // YYYY-MM-DD
  start_time: string;
  end_time: string;
  slot_duration_minutes: number;
  weekdays?: number[] | null;
};

export type AdminProspectSlotsBulkResponseDto = {
  created: number;
  skipped: number;
};

export type AdminProspectCallsMetaItem = { value: string; label: string };

export type AdminProspectCallsMetaResponseDto = {
  motifs: AdminProspectCallsMetaItem[];
  statuses: AdminProspectCallsMetaItem[];
  meeting_platforms: AdminProspectCallsMetaItem[];
  user_types: AdminProspectCallsMetaItem[];
};

export type AdminProspectCallDto = {
  id: string;
  prospect_name: string;
  first_name: string;
  last_name: string;
  company_name: string | null;
  phone: string;
  email: string;
  avatar_url: string | null;
  user_type: string;
  timezone: string;
  email_reminder_enabled: boolean;
  motif: string;
  meeting_platform: string;
  scheduled_date: string; // YYYY-MM-DD
  start_time: string;
  end_time: string;
  status: string;
  meeting_link: string | null;
  google_calendar_event_id: string | null;
  description: string | null;
  slot_id: string | null;
  created_at: string;
  updated_at: string;
};

/** Corps PATCH partiel — n’envoyer que les champs modifiés (valeurs meta pour motif / plateforme / statut). */
export type AdminProspectCallPatchBody = Partial<{
  prospect_name: string;
  first_name: string;
  last_name: string;
  company_name: string | null;
  phone: string;
  email: string;
  motif: string;
  meeting_platform: string;
  scheduled_date: string;
  start_time: string;
  end_time: string;
  status: string;
  description: string | null;
}>;

export type PaginatedAdminProspectCallsResponseDto = {
  count: number;
  page: number;
  page_size: number;
  results: AdminProspectCallDto[];
};

/** Note interne liée à un appel planifié (GET …/calls/{id}/notes). */
export type AdminProspectCallNoteDto = {
  id: string;
  call_id: string;
  author_id: string;
  author_name: string;
  body: string;
  created_at: string;
};

/** POST …/calls/{id}/notes — corps { body }. */
export type AdminProspectCallNoteCreateBody = {
  body: string;
};

